import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchSimulationActivities,
  fetchSimulationMetrics,
  fetchSimulationVehicles,
  fetchUnmatchedRides,
} from '../../../../services/simulation/resultsApi';
import {
  fetchRoadRoutesByResultsIdWithMetadata,
  type RoadRouteWithMetadata,
} from '../../../../services/simulation/resultsPathsService';
import {
  pathDistances,
  rideTimings,
  serviceDay,
  shiftSummaries,
  type RideTiming,
  type ServiceDay,
  type ShiftSummary,
} from '../../../../services/simulation/runReport';
import type {
  ResultsVehicleRow,
  SimulationActivity,
  SimulationRunSummary,
  UnmatchedRide,
} from '../../../../types/simulation';

export type SourceState<T> =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: T }
  | { readonly status: 'error'; readonly error: Error };

export type ReportSource =
  | 'metrics'
  | 'activities'
  | 'unmatched'
  | 'vehicles'
  | 'paths'
  | 'compareMetrics'
  | 'compareActivities'
  | 'compareUnmatched'
  | 'compareVehicles';

export interface RunReportDerived {
  readonly rides: readonly RideTiming[];
  readonly shifts: readonly ShiftSummary[];
  readonly day: ServiceDay | null;
}

export interface RunReport {
  readonly metrics: SourceState<Partial<SimulationRunSummary> | null>;
  readonly activities: SourceState<readonly SimulationActivity[]>;
  readonly unmatched: SourceState<readonly UnmatchedRide[]>;
  readonly vehicles: SourceState<readonly ResultsVehicleRow[]>;
  /** Idle until metrics, activities, unmatched rides and vehicles have all settled. */
  readonly paths: SourceState<Readonly<Record<string, RoadRouteWithMetadata>>>;
  readonly compareMetrics: SourceState<Partial<SimulationRunSummary> | null>;
  /** The comparison run's rides, for the charts; idle until this run's own sources have settled. */
  readonly compareActivities: SourceState<readonly SimulationActivity[]>;
  readonly compareUnmatched: SourceState<readonly UnmatchedRide[]>;
  readonly compareVehicles: SourceState<readonly ResultsVehicleRow[]>;
  readonly derived: RunReportDerived | null;
  readonly compareDerived: RunReportDerived | null;
  readonly distances: ReturnType<typeof pathDistances> | null;
  readonly retry: (source: ReportSource) => void;
}

const IDLE = { status: 'idle' } as const;
const LOADING = { status: 'loading' } as const;

function useSource<T>(runId: string | null, attempt: number, enabled: boolean, load: (runId: string) => Promise<T>): SourceState<T> {
  const [result, setResult] = useState<{ runId: string; attempt: number; state: SourceState<T> } | null>(null);
  useEffect(() => {
    if (!runId || !enabled) return undefined;
    let cancelled = false;
    load(runId).then(
      (data) => {
        if (!cancelled) setResult({ runId, attempt, state: { status: 'ready', data } });
      },
      (error: unknown) => {
        if (!cancelled) {
          setResult({ runId, attempt, state: { status: 'error', error: error instanceof Error ? error : new Error(String(error)) } });
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [runId, attempt, enabled, load]);
  if (!runId || !enabled) return IDLE;
  return result?.runId === runId && result.attempt === attempt ? result.state : LOADING;
}

function deriveRun(activities: SourceState<readonly SimulationActivity[]>, vehicles: SourceState<readonly ResultsVehicleRow[]>): RunReportDerived | null {
  if (activities.status !== 'ready' || vehicles.status !== 'ready') return null;
  const rides = rideTimings(activities.data);
  return { rides, shifts: shiftSummaries(vehicles.data, rides), day: serviceDay(vehicles.data) };
}

const isSettled = (state: SourceState<unknown>) => state.status === 'ready' || state.status === 'error';
/** The simulator can take over a minute to build a large run's paths; the service's 60 s default cuts that off. */
const PATHS_TIMEOUT_MS = 5 * 60_000;
const loadPaths = (runId: string) => fetchRoadRoutesByResultsIdWithMetadata(runId, { throwOnError: true, timeoutMs: PATHS_TIMEOUT_MS });

export function useRunReport(runId: string | null, compareRunId: string | null): RunReport {
  const [attempts, setAttempts] = useState<Record<ReportSource, number>>({
    metrics: 0,
    activities: 0,
    unmatched: 0,
    vehicles: 0,
    paths: 0,
    compareMetrics: 0,
    compareActivities: 0,
    compareUnmatched: 0,
    compareVehicles: 0,
  });
  const retry = useCallback((source: ReportSource) => {
    setAttempts((current) => ({ ...current, [source]: current[source] + 1 }));
  }, []);

  const metrics = useSource(runId, attempts.metrics, true, fetchSimulationMetrics);
  const activities = useSource(runId, attempts.activities, true, fetchSimulationActivities);
  const unmatched = useSource(runId, attempts.unmatched, true, fetchUnmatchedRides);
  const vehicles = useSource(runId, attempts.vehicles, true, fetchSimulationVehicles);
  // Road paths and the comparison's rides wait for this run's own sources, so the report fills in first.
  const [settledRunId, setSettledRunId] = useState<string | null>(null);
  if (runId && settledRunId !== runId && [metrics, activities, unmatched, vehicles].every(isSettled)) {
    setSettledRunId(runId);
  }
  const settled = settledRunId === runId;
  const paths = useSource(runId, attempts.paths, settled, loadPaths);
  const compareMetrics = useSource(compareRunId, attempts.compareMetrics, true, fetchSimulationMetrics);
  const compareActivities = useSource(compareRunId, attempts.compareActivities, settled, fetchSimulationActivities);
  const compareUnmatched = useSource(compareRunId, attempts.compareUnmatched, settled, fetchUnmatchedRides);
  const compareVehicles = useSource(compareRunId, attempts.compareVehicles, settled, fetchSimulationVehicles);

  const derived = useMemo(() => deriveRun(activities, vehicles), [activities, vehicles]);
  const compareDerived = useMemo(() => deriveRun(compareActivities, compareVehicles), [compareActivities, compareVehicles]);

  const distances = useMemo(() => (paths.status === 'ready' ? pathDistances(paths.data) : null), [paths]);

  // One object per change, so memoised cards skip the playback clock's per-frame renders.
  return useMemo(
    () => ({
      metrics,
      activities,
      unmatched,
      vehicles,
      paths,
      compareMetrics,
      compareActivities,
      compareUnmatched,
      compareVehicles,
      derived,
      compareDerived,
      distances,
      retry,
    }),
    [metrics, activities, unmatched, vehicles, paths, compareMetrics, compareActivities, compareUnmatched, compareVehicles, derived, compareDerived, distances, retry]
  );
}
